import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import { motion, MotionConfig } from "motion/react";
import { Header } from "./components/Header";
import { ServiceCards } from "./components/ServiceCards";
import { Message } from "./components/Message";
import { Composer } from "./components/Composer";
import { Thinking } from "./components/Thinking";
import { Sidebar } from "./components/Sidebar";
import { Boundary } from "./components/Boundary";
import { useWide } from "./hooks/useWide";
import { CameraDialog } from "./components/CameraDialog";
import { useChat, PHASES } from "./hooks/useChat";
import { preparePhoto } from "./api/client";

const SIDEBAR_KEY = "ekspertiza.sidebar";
const MAX_PHOTOS = 3;
const EASE = [0.16, 1, 0.3, 1];
// Bosh sahifa ketma-ket ochiladi: logo -> sarlavha -> kartalar -> yozish maydoni
const rise = (delay) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: EASE },
});

export default function App() {
  const {
    messages, trace, phase, busy, error, sessionId, chatKey,
    send, sendPhotos, openVisual, openChat, retryVisual, drawVisual, reset, stop,
  } = useChat();

  const [input, setInput] = useState("");
  const [mode, setMode] = useState(null); // birinchi kirishda hech biri tanlanmagan
  const wide = useWide();
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === "collapsed";
    } catch {
      return false;
    }
  });
  const [photos, setPhotos] = useState([]); // [{file, thumb}]
  const [preparing, setPreparing] = useState(0);
  const [drag, setDrag] = useState(false);
  const [attachMenu, setAttachMenu] = useState(false);
  const [camera, setCamera] = useState(false);

  const endRef = useRef(null);
  const fileRef = useRef(null);
  const shotRef = useRef(null);

  // Brauzerning o'zida (CameraDialog); u yo'q bo'lsa (http) — telefonning kamera ilovasi
  const openCamera = () =>
    navigator.mediaDevices?.getUserMedia ? setCamera(true) : shotRef.current?.click();
  const openFiles = () => fileRef.current?.click();
  const closeCamera = useCallback(() => setCamera(false), []);

  useEffect(() => {
    // Tayyor tasvir o'z boshiga suriladi (VisualCard) — oxiriga emas
    const last = messages.at(-1);
    if (last?.role === "visual" && last.view?.status === "done") return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, phase]);

  // Dashboard havolalari: `/?chat=<sessiya>` yoki `/?visual=<vazifa>`
  const linked = useRef(false);
  useEffect(() => {
    if (linked.current) return;
    linked.current = true;
    const p = new URLSearchParams(window.location.search);
    const chat = p.get("chat");
    const visual = p.get("visual");
    if (!chat && !visual) return;
    window.history.replaceState(null, "", "/");
    if (chat) return void openChat(chat);
    // Suhbatsiz tasvir (tashqi mijozniki) tiklangan suhbat ustiga emas, bo'sh oynaga
    reset();
    openVisual(visual);
  }, [openChat, openVisual, reset]);

  // Ketma-ket: telefonda bir vaqtda uchta 12 MP fotoni ochish xotirani to'ldiradi
  const addPhotos = async (list) => {
    const files = [...list]
      .filter((f) => !f.type || f.type.startsWith("image/"))
      .slice(0, Math.max(0, MAX_PHOTOS - photos.length - preparing));
    for (const f of files) {
      setPreparing((n) => n + 1);
      const p = await preparePhoto(f);
      setPreparing((n) => n - 1);
      setPhotos((ps) => [...ps, p].slice(0, MAX_PHOTOS));
    }
  };

  const removePhoto = (i) => {
    if (photos[i]?.thumb) URL.revokeObjectURL(photos[i].thumb);
    setPhotos((p) => p.filter((_, j) => j !== i));
  };

  const ask = (text) => {
    if (preparing) return;
    if (photos.length && text === undefined) {
      sendPhotos(photos, input);
      setPhotos([]);
      setInput("");
      return;
    }
    const q = (text ?? input).trim();
    if (!q || busy) return;

    setInput("");
    send(q);
  };

  const empty = messages.length === 0;

  const collapse = (v) => {
    setCollapsed(v);
    try {
      localStorage.setItem(SIDEBAR_KEY, v ? "collapsed" : "open");
    } catch {
      /* jim */
    }
  };
  const closeDrawer = () => setDrawer(false);
  // Shu qurilmadan yuborilgan tasvir tugasa/yiqilsa chap paneldagi ro'yxat yangilanadi
  // (tarixdan ochilgani ro'yxatni o'zgartirmaydi)
  const visualKey = messages.filter((m) => m.files && ["done", "failed"].includes(m.view?.status)).length;

  return (
    <MotionConfig reducedMotion="user">
    <div
      onDragEnter={(e) => e.dataTransfer.types.includes("Files") && setDrag(true)}
      className="flex h-full overflow-hidden bg-[#072147] font-sans text-slate-100 selection:bg-sky-500 selection:text-white"
    >
      <div className="pointer-events-none fixed inset-0 z-0 bg-gradient-to-b from-[#07224a] via-[#061c3b] to-[#04142b]" />

      <Boundary>
      <Sidebar
        drawer={drawer}
        onCloseDrawer={closeDrawer}
        collapsed={collapsed}
        onCollapse={() => collapse(true)}
        mode={mode}
        onSelectMode={(m) => {
          setMode(m);
          closeDrawer();
        }}
        onNewChat={() => {
          reset();
          setMode(null);
          closeDrawer();
        }}
        onPhoto={() => {
          closeDrawer();
          setAttachMenu(true);
        }}
        activeChat={sessionId}
        chatKey={chatKey}
        onOpenChat={(id) => {
          closeDrawer();
          openChat(id);
        }}
        onDeletedChat={(id) => id === sessionId && reset()}
        onOpenVisual={(id) => {
          closeDrawer();
          openVisual(id);
        }}
        refreshKey={visualKey}
      />
      </Boundary>

      <div className="relative flex min-w-0 flex-1 flex-col">
        {(!wide || collapsed) && (
          <Header
            hasMessages={!empty}
            onOpenPanel={() => (wide ? collapse(false) : setDrawer(true))}
            onReset={() => {
              reset();
              setMode(null);
            }}
          />
        )}

        <main className="relative z-10 mx-auto flex w-full min-h-0 max-w-4xl flex-1 flex-col px-4 sm:px-6">
          {empty ? (
            <div className="flex flex-1 flex-col justify-center overflow-y-auto py-8 sm:py-12">
              <div className="mb-5 select-none space-y-3 text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 170, damping: 17, delay: 0.05 }}
                  className="relative inline-flex"
                >
                  <motion.span
                    aria-hidden
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: [0, 1, 0.55], scale: 1 }}
                    transition={{ duration: 1.6, delay: 0.1, ease: "easeOut" }}
                    className="pointer-events-none absolute -inset-8 rounded-full bg-sky-500/25 blur-2xl"
                  />
                  <div className="relative inline-flex rounded-2xl border border-sky-500/30 bg-sky-950/70 p-2.5 shadow-xl shadow-sky-950/60 backdrop-blur-sm">
                    <img
                      src="/logo.png"
                      alt="O'zbekekspertiza"
                      className="h-16 w-16 object-contain drop-shadow sm:h-20 sm:w-20"
                    />
                  </div>
                </motion.div>
                <div className="space-y-1">
                  <motion.h2 {...rise(0.18)} className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    Qanday yordam bera olaman?
                  </motion.h2>
                  <motion.p {...rise(0.26)} className="mx-auto max-w-md text-xs text-sky-200/70 sm:text-sm">
                    TIF TN tovar kodini aniqlash va nomenklatura izohlari bo'yicha savol-javob
                  </motion.p>
                </div>
              </div>

              <ServiceCards mode={mode} onSelect={setMode} onPhoto={() => setAttachMenu(true)} />
            </div>
          ) : (
            <div className="feed-scroll min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
              {messages.map((m) => (
                <Boundary key={m.id}>
                <Message message={m} onRetryVisual={retryVisual} onDrawVisual={drawVisual} />
              </Boundary>
              ))}

              {phase === PHASES.THINKING && <Thinking trace={trace} />}

              {error && (
                <div
                  role="alert"
                  className="rounded-xl border border-rose-800/50 bg-rose-950/40 p-3 text-xs text-rose-200"
                >
                  {error}
                </div>
              )}

              <div ref={endRef} />
            </div>
          )}
        </main>

        <motion.footer
          {...rise(0.45)}
          className="relative z-20 shrink-0 bg-gradient-to-t from-[#072147] via-[#072147]/95 to-transparent"
        >
          <Composer
            value={input}
            onChange={setInput}
            onSubmit={ask}
            onStop={stop}
            busy={busy}
            photos={photos}
            preparing={preparing}
            maxPhotos={MAX_PHOTOS}
            menuOpen={attachMenu}
            onMenu={setAttachMenu}
            onCamera={openCamera}
            onFiles={openFiles}
            onAddPhotos={addPhotos}
            onRemovePhoto={removePhoto}
          />
        </motion.footer>
      </div>

      {[[fileRef, {}], [shotRef, { capture: "environment" }]].map(([r, extra], i) => (
        <input
          key={i}
          ref={r}
          type="file"
          accept="image/*"
          multiple={!extra.capture}
          hidden
          {...extra}
          onChange={(e) => {
            addPhotos(e.target.files);
            e.target.value = "";
          }}
        />
      ))}

      {camera && (
        <CameraDialog
          onShot={(f) => addPhotos([f])}
          onClose={closeCamera}
          onFallback={() => {
            setCamera(false);
            openFiles();
          }}
        />
      )}

      {drag && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            addPhotos(e.dataTransfer.files);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#04142b]/85 p-6 backdrop-blur-sm"
        >
          <div className="pointer-events-none flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-sky-400/70 text-sky-100">
            <ImagePlus className="h-14 w-14 text-sky-300" />
            <p className="text-lg font-semibold">Fotoni shu yerga tashlang</p>
          </div>
        </div>
      )}

    </div>
    </MotionConfig>
  );
}
